import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        setCartItems(parsedCart);
      } catch (error) {
        console.error('Error parsing cart from localStorage:', error);
        setCartItems([]);
      }
    } else {
      setCartItems([]);
    }
  }, []);

 
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product) => {
    const existingItem = cartItems.find(item => item.productId === product.productId);
    
    if (!existingItem) {
      const newCartItems = [...cartItems, { ...product, quantity: 1, addedAt: new Date().toISOString() }];
      setCartItems(newCartItems);
      return { success: true, message: 'Product added to cart successfully!' };
    } else {
      // Increment quantity if item already exists
      const newCartItems = cartItems.map(item => 
        item.productId === product.productId 
          ? { ...item, quantity: (item.quantity || 1) + 1 }
          : item
      );
      setCartItems(newCartItems);
      return { success: true, message: 'Product quantity increased!' };
    }
  };

  const removeFromCart = (productId) => {
    const newCartItems = cartItems.filter(item => item.productId !== productId);
    setCartItems(newCartItems);
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    const newCartItems = cartItems.map(item => 
      item.productId === productId 
        ? { ...item, quantity: newQuantity }
        : item
    );
    setCartItems(newCartItems);
  };

  const incrementQuantity = (productId) => {
    const item = cartItems.find(item => item.productId === productId);
    if (item) {
      updateQuantity(productId, (item.quantity || 1) + 1);
    }
  };

  const decrementQuantity = (productId) => {
    const item = cartItems.find(item => item.productId === productId);
    if (item) {
      updateQuantity(productId, (item.quantity || 1) - 1);
    }
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartItemsCount = () => {
    return cartItems.reduce((total, item) => total + (item.quantity || 1), 0);
  };

  const isInCart = (productId) => {
    return cartItems.some(item => item.productId === productId);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    clearCart,
    getCartItemsCount,
    isInCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 